/**
 * A number series' arithmetic, as `app.assign_doc_number()` (migration 0085)
 * does it, for the screens that say "This gives it number INV/26-27/00013"
 * before the database gives it (proposal.md 5.3, D19).
 *
 * The database stays the only thing that gives a number; this only says what
 * the next one will look like.
 *
 * Off the package index, like `doc-layout.ts`.
 */

export const SERIES_FAMILIES = ['invoice', 'bill', 'receipt', 'quotation'] as const;
export type DocSeriesFamily = (typeof SERIES_FAMILIES)[number];
export const SERIES_YEAR_STYLES = ['yy_yy', 'yyyy_yy', 'yy_yy_compact', 'yyyy', 'none'] as const;
export type DocSeriesYearStyle = (typeof SERIES_YEAR_STYLES)[number];
export const SERIES_RESETS = ['fy', 'never'] as const;
export type DocSeriesReset = (typeof SERIES_RESETS)[number];

/** A series' shape: what the database freezes once it has given a number. */
export type DocSeriesShape = {
  readonly family: DocSeriesFamily;
  readonly prefix: string;
  readonly yearStyle: DocSeriesYearStyle;
  readonly digits: number;
  readonly startNumber: number;
  readonly reset: DocSeriesReset;
  readonly fyStartMonth: number;
};

/** The SQL checks of `app.doc_series`, in code, so a form names the problem before the database does. */
export const SERIES_PREFIX = /^[A-Z]([A-Z0-9/-]{0,6}[A-Z/-])?$/;

const YEAR_LENGTH: Readonly<Record<DocSeriesYearStyle, number>> = { yy_yy: 6, yyyy_yy: 8, yy_yy_compact: 5, yyyy: 5, none: 0 };

export type DocSeriesProblem = 'prefix' | 'digits' | 'start' | 'too_long' | 'fy_month' | 'year_style' | 'reset';

/** A series' shape without its document family: what a number series field holds too. */
export type SeriesShape = Omit<DocSeriesShape, 'family'>;

/**
 * How a number series FIELD differs from a document series (W18 of
 * docs/plans/workbook-map.md): its prefix may be empty and its number need not
 * be padded, because a paper voucher prints "41", not "INV/041".
 */
export const FIELD_SERIES_RULES = { prefixMayBeEmpty: true, minDigits: 1 } as const;

/** Every problem of a shape, at once. A document series by default; a field's with `FIELD_SERIES_RULES`. */
export const seriesShapeProblems = (
  shape: SeriesShape,
  rules: { readonly prefixMayBeEmpty?: boolean; readonly minDigits?: number } = {},
): readonly DocSeriesProblem[] => {
  const out: DocSeriesProblem[] = [];
  if (typeof shape.prefix !== 'string' || !((rules.prefixMayBeEmpty === true && shape.prefix === '') || SERIES_PREFIX.test(shape.prefix))) out.push('prefix');
  if (!(SERIES_YEAR_STYLES as readonly string[]).includes(shape.yearStyle)) out.push('year_style');
  if (!Number.isInteger(shape.digits) || shape.digits < (rules.minDigits ?? 3) || shape.digits > 6) out.push('digits');
  if (!Number.isInteger(shape.startNumber) || shape.startNumber < 1 || shape.startNumber > 999_999) out.push('start');
  if (!(SERIES_RESETS as readonly string[]).includes(shape.reset)) out.push('reset');
  if (String(shape.prefix).length + (YEAR_LENGTH[shape.yearStyle] ?? 0) + shape.digits > 16) out.push('too_long');
  if (!Number.isInteger(shape.fyStartMonth) || shape.fyStartMonth < 1 || shape.fyStartMonth > 12) out.push('fy_month');
  else if (shape.fyStartMonth === 1 ? shape.yearStyle !== 'yyyy' && shape.yearStyle !== 'none' : shape.yearStyle === 'yyyy') {
    if (!out.includes('year_style')) out.push('year_style');
  }
  return out;
};

/** The financial year's start year for a day (`YYYY-MM-DD`): 2026 for 22 Oct 2026 with an April year. */
export const fiscalYearOf = (issuedOn: string, fyStartMonth: number): number => {
  const year = Number(issuedOn.slice(0, 4));
  const month = Number(issuedOn.slice(5, 7));
  return month < fyStartMonth ? year - 1 : year;
};

/** The counter period a day falls in: the start year for a yearly series, `all` for one that never restarts. */
export const seriesPeriodOf = (shape: Pick<DocSeriesShape, 'reset' | 'fyStartMonth'>, issuedOn: string): string =>
  shape.reset === 'fy' ? String(fiscalYearOf(issuedOn, shape.fyStartMonth)) : 'all';

const two = (n: number): string => String(n % 100).padStart(2, '0');

/** The number counter value `n` prints on `issuedOn`, exactly as the database writes it. */
export const formatDocNumber = (shape: SeriesShape, issuedOn: string, n: number): string => {
  const start = fiscalYearOf(issuedOn, shape.fyStartMonth);
  const year =
    shape.yearStyle === 'yy_yy'
      ? `${two(start)}-${two(start + 1)}/`
      : shape.yearStyle === 'yyyy_yy'
        ? `${start}-${two(start + 1)}/`
        : shape.yearStyle === 'yy_yy_compact'
          ? `${two(start)}${two(start + 1)}/`
          : shape.yearStyle === 'yyyy'
            ? `${start}/`
            : '';
  const counter = String(n);
  return `${shape.prefix}${year}${counter.length >= shape.digits ? counter : counter.padStart(shape.digits, '0')}`;
};

/** The next number, given the last one the period gave (null when it has given none). */
export const nextDocNumber = (shape: SeriesShape, issuedOn: string, last: number | null): string =>
  formatDocNumber(shape, issuedOn, last === null ? shape.startNumber : last + 1);

/**
 * A year style that fits a financial year starting in `fyStartMonth`: a
 * calendar year prints one year (2026/), any other prints two (26-27/). For a
 * series made with the workspace's year (L42 of docs/plans/ledger.md), when
 * the person picked no year of their own; `none` always fits.
 */
export const yearStyleFor = (yearStyle: DocSeriesYearStyle, fyStartMonth: number): DocSeriesYearStyle => {
  if (yearStyle === 'none') return 'none';
  if (fyStartMonth === 1) return 'yyyy';
  return yearStyle === 'yyyy' ? 'yy_yy' : yearStyle;
};
